'use client';

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useRBAC } from '@/contexts/rbac-context';
import keycloak from '@/lib/keycloak';
import { Loader2, Shield, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  // 메뉴 역할세트 기반 접근 제어 (운영 메뉴). 배열이면 하나라도 있으면 통과 (전자결재 SPA 는 진입 키가 5개)
  requiredMenuId?: string | string[];
  // Custom unauthorized component
  unauthorizedComponent?: React.ComponentType;
}

// Default Unauthorized Component
const DefaultUnauthorizedComponent = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <CardTitle className="text-xl font-semibold">접근 권한이 없습니다</CardTitle>
          <CardDescription>
            이 페이지에 접근할 권한이 없습니다. 관리자에게 문의하세요.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <div className="flex items-center justify-center text-sm text-gray-600">
            <AlertCircle className="mr-2 h-4 w-4" />
            필요한 권한이 부족합니다
          </div>
          <div className="flex gap-2 justify-center">
            <Button variant="outline" onClick={() => navigate(-1)}>
              이전 페이지
            </Button>
            <Button onClick={() => navigate(`/${window.location.pathname.split('/')[1]}`)}>
              메인으로
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export function ProtectedRoute({
  children,
  fallback,
  requiredMenuId,
  unauthorizedComponent: UnauthorizedComponent = DefaultUnauthorizedComponent,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const {
    loadOrganizations,
    isLoading: orgLoading,
    organizations,
  } = useOrganization();
  const { canAccessMenu, loading: menuLoading, error: menuError } = useRBAC();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      keycloak.login();
    }
  }, [isAuthenticated, isLoading]);

  // 인증된 사용자의 조직정보 로드 (인증 세션당 1회만 시도)
  // PENDING 사용자 등으로 조직 조회가 실패해도 organizations가 비어 있어 effect가
  // 무한 재발화하던 문제를 ref 가드로 차단한다 (실패 시 재시도는 새로고침으로).
  const orgAttemptedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated) {
      orgAttemptedRef.current = false;
      return;
    }
    if (!orgAttemptedRef.current && !orgLoading && organizations.length === 0) {
      orgAttemptedRef.current = true;
      loadOrganizations();
    }
  }, [isAuthenticated, loadOrganizations, orgLoading, organizations.length]);

  // 인증/조직/메뉴 로딩 중 스피너
  if (isLoading || (isAuthenticated && orgLoading) || (isAuthenticated && menuLoading)) {
    return (
      fallback || (
        <div className='flex items-center justify-center min-h-screen'>
          <div className='text-center'>
            <Loader2 className='h-8 w-8 animate-spin mx-auto mb-4 text-blue-600' />
            <p className='text-gray-600'>
              {isLoading ? '인증 확인 중...' : orgLoading ? '조직정보 로딩 중...' : '권한 정보 로딩 중...'}
            </p>
          </div>
        </div>
      )
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // 권한 조회가 실패하면 메뉴도 화면도 비어 있게 된다 - 빈 껍데기 대신 이유를 보여준다
  if (menuError) {
    return (
      <div className='flex min-h-screen items-center justify-center p-4'>
        <div className='space-y-3 text-center'>
          <AlertCircle className='mx-auto h-8 w-8 text-red-600' />
          <p className='text-sm text-gray-600'>권한 정보를 불러오지 못했습니다. 잠시 후 다시 시도하세요.</p>
          <Button variant='outline' onClick={() => window.location.reload()}>
            다시 시도
          </Button>
        </div>
      </div>
    );
  }

  // 메뉴 접근 검사
  const requiredMenuIds = requiredMenuId === undefined ? [] : [requiredMenuId].flat();
  if (requiredMenuIds.length > 0 && !requiredMenuIds.some(canAccessMenu)) {
    return <UnauthorizedComponent />;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
